import { PropsWithChildren, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CSRF_TOKEN } from "@pages/csrf";
import { Button, Dialog, Row } from "@narmi/design_system";
import { useUserConfiguration } from "@hooks/useUserConfiguration";

export function SessionGuard({ children }: PropsWithChildren) {
  const [showReauthModal, setShowReauthModal] = useState(false);

  useQuery({
    queryKey: ["sessionHealth"],
    queryFn: async () => {
      const response = await fetch("/api/session/health/", {
        method: "POST",
        redirect: "manual",
        headers: { "Content-Type": "application/json", "X-CSRFToken": CSRF_TOKEN },
      });

      // If the API response is a redirect, the Django session has already expired, so just hard redirect to login
      if (response.status === 0 || response.type === "opaqueredirect") {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
        return null;
      }

      // If we get a 401, the user has a valid Django session but their OIDC token is invalid,
      // so we give them the option to reauthenticate their OIDC session via a modal
      if (response.status === 401) {
        setShowReauthModal(true);
        return null;
      }

      if (!response.ok) {
        throw new Error("Health check failed");
      }

      setShowReauthModal(false);
      return null;
    },
    refetchInterval: 1000 * 60 * 4, // 4 minutes
    refetchOnWindowFocus: "always",
    retry: false,
  });

  return (
    <>
      {children}
      {showReauthModal && <ReauthModal />}
    </>
  );
}

function ReauthModal() {
  const { currentUser } = useUserConfiguration();

  function handleReauthenticate() {
    // In practice, this value should always exist. The endpoint will only return 401 if the user is logged in via SSO.
    if (currentUser.session_oidc_provider) {
      window.location.href = `/oidc/${currentUser.session_oidc_provider}/redirect?next=${encodeURIComponent(
        window.location.pathname
      )}`;
    }
  }

  function logout() {
    window.location.href = "/logout";
  }

  return (
    <Dialog
      title="Session expired"
      onUserDismiss={logout}
      isOpen
      footer={
        <Row justifyContent="end" alignItems="center">
          <Row.Item shrink>
            <Button label="Log out" kind="plain" onClick={logout} />
          </Row.Item>
          <Row.Item shrink>
            <Button label="Reauthenticate" onClick={handleReauthenticate} />
          </Row.Item>
        </Row>
      }
    >
      <div>Your session has expired. Please reauthenticate to continue.</div>
    </Dialog>
  );
}
